import {groupBy, tail, uniq} from 'lodash';

import {useTimezone} from 'src/components/TimezoneContext';
import {Site} from 'src/lib/api/project';
import {ApiForecastTimeSeries, ApiObservationTimeSeries} from 'src/lib/api/series';
import {getDataByTs} from 'src/lib/dataUtils';

type Props = {
  title: string;
  observationTimeSeriesList: ApiObservationTimeSeries[];
  forecastTimeSeriesList: ApiForecastTimeSeries[];
  site: Site;
};

function roundValue(value: number): string {
  return value.toFixed(2);
}

const TABLE_PREFIX_BLOCKLIST: string[] = [
  // TODO, optional
];

function getColumnBgClassName(index: number) {
  return index % 2 === 0 ? 'bg-gray-600' : 'bg-gray-700';
}

export default function VizTable({
  title,
  observationTimeSeriesList,
  forecastTimeSeriesList,
  site,
}: Props) {
  const {getDisplayTimeZone} = useTimezone();
  const displayTimeZone = getDisplayTimeZone(site.localTimeZone);
  const dataByTs = getDataByTs(
    site,
    observationTimeSeriesList,
    forecastTimeSeriesList,
    displayTimeZone
  );
  const columnHeaders = uniq(Object.keys(dataByTs).flatMap((t) => Object.keys(dataByTs[t]))).filter(
    (k) => !TABLE_PREFIX_BLOCKLIST.some((blockPrefix) => k.startsWith(blockPrefix))
  );
  const columnGroups = groupBy(columnHeaders, (header) => header.split('_')[0]);
  const columnGroupKeys = Object.keys(columnGroups);

  const tableHeaderClassName = 'z-10 text-sm text-gray-50 px-4 py-2 text-left';

  return (
    <table className="min-w-full">
      <thead>
        <tr>
          {columnGroupKeys.map((g, i) => (
            <th
              key={i}
              colSpan={columnGroups[g].length}
              className={`${tableHeaderClassName} ${getColumnBgClassName(i)} font-black`}
            >
              {g}
            </th>
          ))}
        </tr>
        <tr>
          {columnGroupKeys.map((g, i) => {
            return columnGroups[g].map((header) => {
              const columnParts = header.split('_');
              const columnGroupIndex = columnGroupKeys.indexOf(columnParts[0]);
              return (
                <th
                  key={i}
                  scope="col"
                  className={`sticky top-0 ${i === 0 ? 'left-0' : ''}
               ${tableHeaderClassName} ${getColumnBgClassName(columnGroupIndex)} font-medium`}
                >
                  {tail(columnParts).join(' ')}
                </th>
              );
            });
          })}
        </tr>
      </thead>
      <tbody className="mt-16">
        {Object.keys(dataByTs).map((ts, i) => {
          const rowData = dataByTs[ts];
          const bgClassName = i % 2 === 0 ? 'bg-white' : 'bg-gray-100';
          return (
            <tr key={ts} className="border-b">
              {columnHeaders.map((key, i) => {
                let value = rowData[key];

                if (value && typeof value === 'number') {
                  value = roundValue(value);
                }

                return (
                  <td
                    key={key}
                    className={`${
                      i === 0 ? 'sticky left-0' : ''
                    } ${bgClassName} px-6 py-4 whitespace-nowrap text-sm text-gray-900`}
                  >
                    {value}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
